import createApp from "@shopify/app-bridge";
import { Redirect } from "@shopify/app-bridge/actions";
import { SHOPIFY_API_KEY } from "../config/env";

/**
 * Path for App Bridge ADMIN_PATH: must NOT include `/store/{handle}/` — the host already
 * applies store context, otherwise you get `/store/x/store/x/themes/...`.
 */
function adminPathWithoutStorePrefix(adminUrl) {
  const u = new URL(adminUrl);
  if (u.hostname !== "admin.shopify.com") return null;
  const stripped = u.pathname.replace(/^\/store\/[^/]+(?=\/|$)/, "") || "/";
  const path = `${stripped.startsWith("/") ? stripped : `/${stripped}`}${u.search}${u.hash}`;
  return path;
}

/** Convert `https://{shop}.myshopify.com/admin/...` to an admin path usable with ADMIN_PATH. */
function adminPathFromMyshopifyAdminUrl(adminUrl) {
  const u = new URL(adminUrl);
  if (!u.hostname.endsWith(".myshopify.com")) return null;
  if (!u.pathname.startsWith("/admin/")) return null;
  const stripped = u.pathname.replace(/^\/admin(?=\/|$)/, "") || "/";
  return `${stripped.startsWith("/") ? stripped : `/${stripped}`}${u.search}${u.hash}`;
}

/**
 * Theme editor + other Shopify Admin links: prefer real browser navigation with the full URL.
 * App Bridge REMOTE / ADMIN_PATH with a full `/store/...` path often duplicates the store segment.
 */
export function openRemoteUrl(url, options = {}) {
  if (!url) return;
  const { newContext = true } = options;

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    parsed = null;
  }

  const isAdminShopifyHost =
    parsed && parsed.hostname === "admin.shopify.com";
  const isMyshopifyAdmin =
    parsed &&
    typeof parsed.hostname === "string" &&
    parsed.hostname.endsWith(".myshopify.com") &&
    parsed.pathname.includes("/admin/");

  if (isAdminShopifyHost || isMyshopifyAdmin) {
    // In an embedded app, touching `window.top.location` is cross-origin and will throw.
    // Prefer App Bridge redirects for admin links.
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const host = urlParams.get("host");
      if (host && SHOPIFY_API_KEY) {
        const app = createApp({ apiKey: SHOPIFY_API_KEY, host });
        const redirect = Redirect.create(app);
        // ADMIN_PATH navigates the parent Shopify Admin (avoids X-Frame-Options DENY inside iframe).
        if (isAdminShopifyHost) {
          const path = adminPathWithoutStorePrefix(url);
          if (path) {
            redirect.dispatch(Redirect.Action.ADMIN_PATH, { path, newContext });
            return;
          }
        }
        if (isMyshopifyAdmin) {
          const path = adminPathFromMyshopifyAdminUrl(url);
          if (path) {
            redirect.dispatch(Redirect.Action.ADMIN_PATH, { path, newContext });
            return;
          }
        }
        // Fallback: remote navigation (may open in a new context depending on host policies).
        redirect.dispatch(Redirect.Action.REMOTE, { url, newContext });
        return;
      }
    } catch (e0) {
      console.warn("[openRemoteUrl] App Bridge REMOTE for admin url:", e0);
    }

    // If App Bridge config is missing, do NOT navigate the embedded iframe to Shopify Admin
    // (it will be blocked by X-Frame-Options=DENY). Open a new tab instead.
    try {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    } catch (e) {
      console.warn("[openRemoteUrl] window.open failed for admin url:", e);
    }

    if (isAdminShopifyHost) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const host = urlParams.get("host");
        const bridgePath = adminPathWithoutStorePrefix(url);
        if (host && SHOPIFY_API_KEY && bridgePath) {
          const app = createApp({ apiKey: SHOPIFY_API_KEY, host });
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.ADMIN_PATH, {
            path: bridgePath,
            newContext,
          });
          return;
        }
      } catch (e2) {
        console.warn("[openRemoteUrl] App Bridge ADMIN_PATH fallback:", e2);
      }
    }

    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    return;
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get("host");
    if (host && SHOPIFY_API_KEY) {
      const app = createApp({ apiKey: SHOPIFY_API_KEY, host });
      const redirect = Redirect.create(app);
      redirect.dispatch(Redirect.Action.REMOTE, {
        url,
        newContext,
      });
      return;
    }
  } catch (e) {
    console.warn("[openRemoteUrl] App Bridge redirect:", e);
  }
  try {
    window.location.href = url;
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}
